import Box from '@mui/material/Box';
import { Fragment, useContext, useState } from 'react';
import Maybe from 'true-myth/maybe';

import { TableBar } from './TableBar';
import { TableBarSearch } from './TableBarSearch';
import { usePersistTableSort } from './use-persist-table-sort';
import { defaultPagination } from './use-talent-directory-params';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import { ContentWrapper } from '../../../design/components/containers/ContentWrapper';
import { useTablePagination } from '../../common/data-grids/common/use-table-pagination';
import { LearningOpportunitiesDataGrid } from '../../common/data-grids/learning-opportunities/LearningOpportunitiesDataGrid';
import { useLearningOpportunitiesSearchColumns } from '../../common/data-grids/learning-opportunities/UseLearningOpportunitiesSearchColumns';
import { useNavigationToEntity } from '../../common/use-navigation-to-entity';
import { ComponentErrorBoundary } from '../../error-handling/ErrorBoundaries';
import { useSnackbars } from '../../error-handling/SnackBars';
import { useTalentDirectoryLearningOpportunityExport } from '../../query-hooks/talent-directory/talent-directory';
import { useGlobalSearchDirectoryLearningOpportunities } from '../../query-hooks/talent-directory/use-global-search-directory-learning-opportunities';
import { useTalentDirectoryLearningOpportunityFilter } from '../../query-hooks/use-talent-directory-learning-opportunity-filter';

import type { LearningOpportunitySearchRow } from '../../common/data-grids/learning-opportunities/LearningOpportunitiesDataGrid';
import type { FunctionComponent } from 'react';

export const LearningOpportunitiesTable: FunctionComponent = () => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const { paginationModel, setPaginationModel } = useTablePagination();
    const { navigateToLearningOpportunityPage } = useNavigationToEntity();
    const [sortOptions, saveSortOptions] = usePersistTableSort('talentDirectoryLearningOpportunitySort');
    const searchTerm = Maybe.of(applicationState.locationSearchParams.get('search'));
    const [isSearchFieldOpen, setIsSearchFieldOpen] = useState<boolean>(searchTerm.isJust);
    const filterQuery = useTalentDirectoryLearningOpportunityFilter({
        enabled: true,
    });
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();
    const { isPending: isExporting, mutate: exportLearningOpportunities } =
        useTalentDirectoryLearningOpportunityExport();
    const onExportButtonClick = (): void => {
        exportLearningOpportunities(undefined, {
            onError: showErrorSnackbar,
            onSuccess: ({ downloadLink }) => {
                showSuccessSnackbar('success.learning-opportunities.exported');
                window.open(downloadLink, '_blank');
            },
        });
    };
    const { data, isFetching, isPending } = useGlobalSearchDirectoryLearningOpportunities({
        filterPayload: filterQuery.data?.filterPayload ?? Maybe.nothing(),
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        searchTerm,
        sortOptions,
    });

    const columns = useLearningOpportunitiesSearchColumns();

    const learningOpportunities = data?.data ?? [];
    const pagination = data?.pagination ?? defaultPagination;

    const rows: readonly LearningOpportunitySearchRow[] = learningOpportunities.map((learningOpportunity) => ({
        id: learningOpportunity.id,
        createdAt: learningOpportunity.createdAt,
        skills: learningOpportunity.skills,
        title: learningOpportunity.title,
    }));

    const noDataMessage = translate('pages.global-search.no-data.learning-opportunities');

    return (
        <Fragment>
            {filterQuery.isSuccess && (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        justifyContent: 'flex-end',
                        minHeight: '56px',
                        px: 2,
                    }}
                >
                    {isSearchFieldOpen ? (
                        <TableBarSearch onSearchBarCancelButtonClick={() => setIsSearchFieldOpen(false)} />
                    ) : (
                        <TableBar
                            filters={filterQuery.data.filters}
                            hasActiveFilters={filterQuery.data.hasActiveFilters}
                            isExporting={isExporting}
                            resetFilterValues={filterQuery.data.resetFilterValues}
                            saveFilterValues={filterQuery.data.saveFilterValues}
                            onExportButtonClick={onExportButtonClick}
                            onSearchButtonClick={() => setIsSearchFieldOpen(true)}
                        />
                    )}
                </Box>
            )}
            <ContentWrapper>
                <ComponentErrorBoundary message="errors.component.global-search.learning-opportunities">
                    <LearningOpportunitiesDataGrid
                        columns={columns}
                        isFetching={isFetching}
                        isPending={isPending}
                        noDataMessage={noDataMessage}
                        paginationModel={paginationModel}
                        rowCount={pagination.count}
                        rows={rows}
                        sortOptions={sortOptions}
                        onPaginationModelChange={setPaginationModel}
                        onRowClick={navigateToLearningOpportunityPage}
                        onSortOptionsChange={saveSortOptions}
                    />
                </ComponentErrorBoundary>
            </ContentWrapper>
        </Fragment>
    );
};
